import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  FileText,
  Calendar,
  TrendingUp,
  ExternalLink,
  BookOpen,
  Hash,
  Star,
  X,
  Image as ImageIcon,
} from "lucide-react";
import { getAuthenticatedAxios } from "../utils/api";
import LoadingLogo from "../components/LoadingLogo";
import highlightService from "../services/highlightService";
import Header from "../components/Header";
import { splitPapersForDisplay } from "../utils/paperFilters";
import CollapsibleLowScoringSection from "../components/CollapsibleLowScoringSection";
import MarkdownText from "../shared/ui/MarkdownText";
import { getCriteriaSelectionLines } from "../features/search/utils/historyCriteria";
import {
  getHistoryCreditsLabel,
  getHistorySearchModeLabel,
  getHistorySearchTypeLabel,
  shouldShowHistorySearchType,
} from "../features/search/utils/historySearchMetadata";
import { normalizeHistoryDetails } from "../features/search/utils/historyPaperNormalization";
import {
  buildSearchHistoryOpenEngagementEvent,
  enqueueResultEngagementEvent,
} from "../services/resultEngagementService";

function SearchDetails() {
  const { searchId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchDetails, setSearchDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightedPapers, setHighlightedPapers] = useState(new Set());
  const [selectedImage, setSelectedImage] = useState(null);
  const recordedHistoryOpenSearchIdRef = useRef(null);

  // Load search details
  const loadSearchDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const authAxios = getAuthenticatedAxios();
      const response = await authAxios.get(
        `/api/v1/search-history/${searchId}/details`,
      );
      const normalizedDetails = normalizeHistoryDetails(response.data);
      setSearchDetails(normalizedDetails);
      const normalizedSearchId =
        normalizedDetails.id || normalizedDetails.search_id || searchId;
      if (recordedHistoryOpenSearchIdRef.current !== normalizedSearchId) {
        recordedHistoryOpenSearchIdRef.current = normalizedSearchId;
        enqueueResultEngagementEvent(
          buildSearchHistoryOpenEngagementEvent({
            searchDetails: {
              ...normalizedDetails,
              id: normalizedSearchId,
            },
            surface: "search_history_detail",
            entryPoint: "history_page_detail",
          }),
        );
      }

      // Load highlights for this search
      try {
        const highlights = await highlightService.getSearchHighlights(searchId);
        // The highlights object is in format: { "pmid1": true, "pmid2": true }
        // We need to extract the PMIDs (keys) from this object
        if (highlights && typeof highlights === "object") {
          const pmids = Object.keys(highlights).filter(
            (key) => highlights[key] === true,
          );
          setHighlightedPapers(new Set(pmids));
        } else {
          setHighlightedPapers(new Set());
        }
      } catch (highlightError) {
        console.warn("Failed to load highlights:", highlightError);
        setHighlightedPapers(new Set());
      }
    } catch (err) {
      console.error("Failed to load search details:", err);
      setError(`${t("common.error")}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [searchId, t]);

  useEffect(() => {
    loadSearchDetails();
  }, [loadSearchDetails]);

  // Toggle paper highlight
  const toggleHighlight = async (pmid) => {
    try {
      const isHighlighted = highlightedPapers.has(pmid);

      if (isHighlighted) {
        await highlightService.removeHighlight(searchId, pmid);
        setHighlightedPapers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(pmid);
          return newSet;
        });
      } else {
        await highlightService.addHighlight(searchId, pmid);
        setHighlightedPapers((prev) => new Set([...prev, pmid]));
      }
    } catch (err) {
      console.error("Failed to toggle highlight:", err);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const criteriaLines = getCriteriaSelectionLines({
    criteriaSet: searchDetails?.criteria_set,
    language: i18n.language,
  });
  const isProMode = String(searchDetails?.search_mode || "").trim().toLowerCase() === "pro";
  const modeLabel = getHistorySearchModeLabel({
    searchMode: searchDetails?.search_mode,
    t,
  });
  const creditsLabel = getHistoryCreditsLabel({
    chargedCredits: searchDetails?.charged_credits,
    t,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <LoadingLogo size="lg" message={t("searchHistory.loadingDetails")} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-red-800 font-semibold">{t("common.error")}</h2>
            <p className="mt-2 text-red-600">{error}</p>
            <button
              onClick={() => navigate("/search-history")}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              {t("common.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/search-history")}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t("searchHistory.backToHistory")}
        </button>

        {/* Search Details Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t("searchHistory.searchDetails")}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("searchHistory.searchQuery")}
              </label>
              <p className="mt-1 text-gray-900">{searchDetails?.query}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("searchHistory.date")}
              </label>
              <p className="mt-1 text-gray-900">
                <Calendar className="h-4 w-4 inline mr-2" />
                {formatDate(searchDetails?.created_at)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("searchHistory.mode")}
              </label>
              {modeLabel ? (
                <span className="mt-1 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700">
                  {modeLabel}
                </span>
              ) : (
                <p className="mt-1 text-gray-400">-</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("searchHistory.credits")}
              </label>
              <p className="mt-1 text-gray-900">{creditsLabel || "-"}</p>
            </div>

            {shouldShowHistorySearchType(searchDetails) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("searchHistory.preference")}
                </label>
                <span className="mt-1 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                  {getHistorySearchTypeLabel({ search: searchDetails, t })}
                </span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("searchHistory.results")}
              </label>
              <p className="mt-1 text-gray-900">
                <FileText className="h-4 w-4 inline mr-2" />
                {searchDetails?.papers?.length || 0} {t("searchHistory.papers")}
              </p>
            </div>
          </div>

          {criteriaLines.length > 0 && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("searchHistory.selectedCriteria")}
              </label>
              <div className="bg-gray-50 rounded-lg p-4">
                <ul className="list-disc list-inside space-y-1">
                  {criteriaLines.map((line, index) => (
                    <li key={`${line}-${index}`} className="text-sm text-gray-700">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Search Queries Used */}
          {searchDetails?.queries && searchDetails.queries.length > 0 && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("searchHistory.queries")} ({searchDetails.queries.length})
              </label>
              <div className="bg-gray-50 rounded-lg p-4">
                <ul className="list-disc list-inside space-y-1">
                  {searchDetails.queries.map((q, idx) => (
                    <li key={idx} className="text-sm text-gray-700">
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* AI Overview */}
          {searchDetails?.ai_overview && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("app.aiOverview.title")}
              </label>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {searchDetails.ai_overview.text_blocks?.map((block, idx) => {
                  if (block.type === "heading") {
                    return (
                      <h4 key={idx} className="text-sm font-semibold text-gray-800">
                        <MarkdownText content={block.snippet} inline />
                      </h4>
                    );
                  }
                  if (block.type === "list") {
                    return (
                      <ul key={idx} className="list-disc list-inside space-y-1 text-sm text-gray-700">
                        {block.list?.map((item, itemIdx) => (
                          <li key={itemIdx}>
                            <MarkdownText content={item.snippet || item} inline />
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <MarkdownText
                      key={idx}
                      content={block.snippet}
                      className="text-sm text-gray-700"
                    />
                  );
                })}

                {!searchDetails.ai_overview.text_blocks &&
                  typeof searchDetails.ai_overview === "string" && (
                    <MarkdownText
                      content={searchDetails.ai_overview}
                      className="text-sm text-gray-700"
                    />
                  )}

                {searchDetails.ai_overview.references?.length > 0 && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-2">
                      {t("app.aiOverview.sources")}
                    </p>
                    <div className="space-y-1">
                      {searchDetails.ai_overview.references.map((ref) => {
                        const refIndex = Number(ref.index);
                        const displayIndex = Number.isFinite(refIndex)
                          ? refIndex + 1
                          : ref.index;
                        return (
                          <a
                            key={ref.index}
                            href={ref.link || "#"}
                            target={ref.link ? "_blank" : undefined}
                            rel={ref.link ? "noopener noreferrer" : undefined}
                            className="block text-xs text-blue-600 hover:text-blue-800 truncate"
                            title={ref.title}
                          >
                            [{displayIndex}] {ref.title}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Papers */}
        {searchDetails?.papers && searchDetails.papers.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t("searchHistory.papers")} ({searchDetails.papers.length})
            </h2>

            <div className="space-y-4">
              {/* Split papers and sort each group with highlights first */}
              {(() => {
                // Split by score threshold
                const { expandedPapers, collapsedPapers } = splitPapersForDisplay(
                  searchDetails.papers,
                  0.5,
                );

                // Sort function to show highlighted papers first
                const sortByHighlight = (papers) => {
                  return papers.sort((a, b) => {
                    const pmidA = a.pmid || a.PMID;
                    const pmidB = b.pmid || b.PMID;
                    const isHighlightedA = highlightedPapers.has(pmidA);
                    const isHighlightedB = highlightedPapers.has(pmidB);

                    // Highlighted papers come first
                    if (isHighlightedA && !isHighlightedB) return -1;
                    if (!isHighlightedA && isHighlightedB) return 1;
                    return 0;
                  });
                };

                // Function to render a single paper card
                const renderPaperCard = (paper, idx) => {
                  const pmid = paper.pmid || paper.PMID;
                  const isHighlighted = highlightedPapers.has(pmid);

                  return (
                    <div
                      key={pmid || idx}
                      className={`border rounded-lg p-4 ${
                        isHighlighted
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900 flex-1">
                          {paper.title}
                        </h3>
                        <button
                          onClick={() => toggleHighlight(pmid)}
                          className={`p-2 rounded-lg transition-colors ${
                            isHighlighted
                              ? "text-yellow-600 bg-yellow-100 hover:bg-yellow-200"
                              : "text-gray-400 hover:text-yellow-600 hover:bg-yellow-50"
                          }`}
                          title={
                            isHighlighted
                              ? "Remove highlight"
                              : "Highlight paper"
                          }
                        >
                          <Star
                            className={`h-5 w-5 ${
                              isHighlighted
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-400"
                            }`}
                          />
                        </button>
                      </div>

                      {/* Figure Images Gallery - Right below title for visibility */}
                      {paper.image_urls && paper.image_urls.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <ImageIcon className="h-4 w-4 text-gray-600" />
                            <h4 className="text-sm font-medium text-gray-700">
                              {t("app.figures")} ({paper.image_urls.length})
                            </h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {paper.image_urls
                              .slice(0, 5)
                              .map((imgUrl, imgIdx) => (
                                <div
                                  key={imgIdx}
                                  className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-teal-500 transition-all cursor-pointer group"
                                  onClick={() => setSelectedImage(imgUrl)}
                                >
                                  <img
                                    src={imgUrl}
                                    alt={`Figure ${imgIdx + 1}`}
                                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.parentElement.innerHTML =
                                        '<div class="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">Failed to load</div>';
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300" />
                                </div>
                              ))}
                            {paper.image_urls.length > 5 && (
                              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500 text-xs font-medium">
                                +{paper.image_urls.length - 5} more
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Paper Info Grid */}
                      {(paper.research_question ||
                        paper.design ||
                        paper.population ||
                        paper.exposure_intervention ||
                        paper.endpoint ||
                        paper.statistics) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                          {paper.research_question && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                {t("searchHistory.researchQuestion")}
                              </span>
                              <MarkdownText
                                content={paper.research_question}
                                className="mt-1 text-sm text-gray-900"
                              />
                            </div>
                          )}
                          {paper.design && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                {t("searchHistory.design")}
                              </span>
                              <MarkdownText
                                content={paper.design}
                                className="mt-1 text-sm text-gray-900"
                              />
                            </div>
                          )}
                          {paper.population && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                {t("searchHistory.population")}
                              </span>
                              <MarkdownText
                                content={paper.population}
                                className="mt-1 text-sm text-gray-900"
                              />
                            </div>
                          )}
                          {paper.exposure_intervention && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                {t("searchHistory.exposureIntervention")}
                              </span>
                              <MarkdownText
                                content={paper.exposure_intervention}
                                className="mt-1 text-sm text-gray-900"
                              />
                            </div>
                          )}
                          {paper.endpoint && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                {t("searchHistory.endpoint")}
                              </span>
                              <MarkdownText
                                content={paper.endpoint}
                                className="mt-1 text-sm text-gray-900"
                              />
                            </div>
                          )}
                          {paper.statistics && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                {t("searchHistory.statistics")}
                              </span>
                              <MarkdownText
                                content={paper.statistics}
                                className="mt-1 text-sm text-gray-900"
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Key results */}
                      {paper.key_results && (
                        <div className="mb-4">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {t("searchHistory.keyResults")}
                          </span>
                          <MarkdownText
                            content={paper.key_results}
                            className="mt-1 text-sm text-gray-700"
                          />
                        </div>
                      )}

                      {/* Selection Rationale */}
                      {(paper.rationale || paper.selection_rationale) && (
                        <div className="mb-4 p-3 bg-teal-50 rounded-lg">
                          <span className="text-xs font-medium text-teal-700 uppercase tracking-wide">
                            {t("searchHistory.selectionRationale")}
                          </span>
                          <MarkdownText
                            content={paper.rationale || paper.selection_rationale}
                            className="mt-1 text-sm text-teal-900"
                          />
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 border-t pt-3">
                        {paper.journal && (
                          <div className="flex items-center">
                            <BookOpen className="h-4 w-4 mr-1" />
                            {paper.journal}
                          </div>
                        )}
                        {paper.pub_year && (
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {paper.pub_year}
                          </div>
                        )}
                        {paper.cited_by !== undefined && (
                          <div className="flex items-center">
                            <TrendingUp className="h-4 w-4 mr-1" />
                            {paper.cited_by} citations
                          </div>
                        )}
                        {pmid && (
                          <div className="flex items-center">
                            <Hash className="h-4 w-4 mr-1" />
                            PMID: {pmid}
                          </div>
                        )}
                        {paper.relevance_score !== undefined && paper.relevance_score !== null && (
                          <div className="flex items-center text-green-600 font-medium">
                            {isProMode ? (
                              <>
                                {t("dashboard.card.criteriaMatchLabel")}
                                <span className="ml-1">
                                  {Number(paper.relevance_score).toFixed(1)}/10
                                </span>
                              </>
                            ) : (
                              <>
                                {t("dashboard.table.score")}: {Number(paper.relevance_score).toFixed(1)}/10
                              </>
                            )}
                        </div>
                      )}
                        <div className="ml-auto flex items-center gap-2">
                          {pmid && (
                            <a
                              href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-teal-600 hover:text-teal-800"
                            >
                              {t("searchHistory.viewOnPubMed")}
                              <ExternalLink className="h-4 w-4 ml-1" />
                            </a>
                          )}
                          {paper.pmcid && (
                            <a
                              href={`https://pmc.ncbi.nlm.nih.gov/articles/${paper.pmcid}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-teal-600 hover:text-teal-800"
                            >
                              {t("app.viewFullText")}
                              <BookOpen className="h-4 w-4 ml-1" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                };

                // Render high-scoring and low-scoring sections
                return (
                  <>
                    {/* High-scoring papers */}
                    {sortByHighlight(expandedPapers).map((paper, idx) =>
                      renderPaperCard(paper, idx),
                    )}

                    {/* Low-scoring papers in collapsible section */}
                    {collapsedPapers.length > 0 && (
                      <CollapsibleLowScoringSection
                        papers={sortByHighlight(collapsedPapers)}
                        renderPaper={(paper, idx) =>
                          renderPaperCard(paper, expandedPapers.length + idx)
                        }
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
              aria-label="Close"
            >
              <X className="h-6 w-6 text-gray-800" />
            </button>
            <img
              src={selectedImage}
              alt="Enlarged figure"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchDetails;
